import { useCallback, useEffect, useRef, useState } from "react";
import { queryClient } from "../queries";
import { connectLedgerEvents, type LedgerEvent } from "../sse";

const FEED_LIMIT = 30;
/** Events arrive in bursts (a reduction touches hundreds of rows); one refetch per burst. */
const COALESCE_MS = 250;

type QueryKey = readonly unknown[];

/**
 * Collapse a burst of events into one round of targeted refetches.
 *
 * The push stream is chatty by design — a single write can emit several events —
 * so refetching on every event would be a self-inflicted stampede. Keys are
 * deduplicated and flushed on a short timer; a `resync` (or any unknown event)
 * falls back to refetching everything.
 */
function useCoalescedInvalidator() {
  const pending = useRef(new Map<string, QueryKey>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    const keys = [...pending.current.values()];
    pending.current.clear();
    for (const key of keys) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }, []);

  const schedule = useCallback(
    (keys: QueryKey[]) => {
      for (const key of keys) pending.current.set(JSON.stringify(key), key);
      if (timer.current == null) timer.current = setTimeout(flush, COALESCE_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timer.current != null) clearTimeout(timer.current);
    },
    [],
  );

  return schedule;
}

const GLOBAL_KEYS: QueryKey[] = [
  ["runs"],
  ["pending-reviews"],
  ["go-no-go"],
  ["green-wall"],
  ["coverage-trend"],
  ["section-trend"],
  ["latest-regression"],
  ["findings"],
  ["run-signals"],
  ["triage-queue"],
  ["finding-funnel"],
  ["inbox"],
  ["run-facets"],
];

function runKeys(runId: string): QueryKey[] {
  return [
    ["run", runId],
    ["go-no-go", runId],
    ["readiness", runId],
    ["scenarios", runId],
    ["pending-run", runId],
    ["gaps", runId],
    ["summary", runId],
    ["live-report", runId],
    ["reports", runId],
    ["findings", runId],
    ["sitting", runId],
  ];
}

/** Which query keys an event invalidates. Unknown types fall back to the global set. */
function keysForEvent(event: LedgerEvent): QueryKey[] {
  const runId = event.runId ?? null;
  const keys: QueryKey[] = [];
  switch (event.type) {
    case "run_created":
    case "run_status_changed":
    case "reduction_complete":
      keys.push(
        ["runs"],
        ["latest-regression"],
        ["coverage-trend"],
        ["section-trend"],
        ["green-wall"],
        ["run-facets"],
      );
      break;
    case "result_recorded":
      keys.push(
        ["runs"],
        ["pending-reviews"],
        ["latest-regression"],
        ["coverage-trend"],
        ["green-wall"],
      );
      if (runId) {
        keys.push(["scenarios", runId], ["summary", runId], ["gaps", runId], ["sitting", runId]);
      }
      break;
    case "coverage_updated":
      keys.push(["runs"], ["latest-regression"], ["coverage-trend"], ["green-wall"]);
      if (runId) keys.push(["summary", runId], ["gaps", runId], ["go-no-go", runId]);
      break;
    case "verdict_created":
    case "verdict_reviewed":
    case "verdict_updated":
    case "verdict_pending":
      keys.push(["pending-reviews"], ["runs"]);
      if (runId) keys.push(["pending-run", runId], ["go-no-go", runId], ["summary", runId]);
      break;
    case "scenario_changed":
      keys.push(["runs"]);
      if (runId) keys.push(["scenarios", runId], ["sitting", runId]);
      break;
    case "finding_changed":
    case "finding_graduated":
      keys.push(["findings"]);
      if (runId) keys.push(["findings", runId]);
      break;
    case "report_changed":
    case "report_published":
      if (runId) keys.push(["reports", runId], ["live-report", runId]);
      break;
    case "notification_created":
    case "notification_changed":
      keys.push(["inbox"]);
      if (runId) keys.push(["run", runId]);
      break;
    case "run_owners_changed":
      keys.push(["inbox"]);
      if (runId) keys.push(["run", runId]);
      break;
    default:
      return [...GLOBAL_KEYS];
  }
  if (runId) keys.push(["run", runId], ["go-no-go", runId]);
  return keys;
}

export function useGlobalLedgerEvents() {
  const [events, setEvents] = useState<LedgerEvent[]>([]);
  const schedule = useCoalescedInvalidator();

  useEffect(() => {
    return connectLedgerEvents("/api/events", (event) => {
      if (event.type === "resync") {
        schedule([[]]); // the root key matches every query
        return;
      }
      schedule(keysForEvent(event));
      setEvents((prev) => [event, ...prev].slice(0, FEED_LIMIT));
    });
  }, [schedule]);

  return events;
}

export function useRunLedgerEvents(runId: string | undefined) {
  const schedule = useCoalescedInvalidator();

  useEffect(() => {
    if (!runId) return;
    return connectLedgerEvents(`/api/runs/${runId}/events`, (event) => {
      if (event.type === "resync") {
        schedule([[]]);
        return;
      }
      schedule([...runKeys(runId), ...keysForEvent(event)]);
    });
  }, [runId, schedule]);
}
